/*
 * Calendar — Seleccionador de fecha y hora
 * ─────────────────────────────────────────────────────────────────
 *
 * Props:
 *   label        string   Etiqueta sobre el input
 *   value        string|Date  Valor controlado. Formatos aceptados:
 *                            "yyyy-mm-dd hh:mm:ss"  (formato nativo de salida)
 *                            "yyyy-mm-ddThh:mm:ss"  (ISO local)
 *                            "yyyy-mm-ddThh:mm:ss.sssZ" (ISO UTC)
 *                            new Date(...)          (objeto Date)
 *   onChange     fn       Recibe el nuevo valor en formato: yyyy-mm-dd (sin withTime) o yyyy-mm-dd hh:mm:ss (con withTime)
 *   withTime     bool     Muestra selector de hora al lado del calendario (default: false)
 *   disabled     bool     Deshabilita el componente
 *   error        bool     Activa estilos de error externos
 *   errorMessage string   Texto del mensaje de error externo
 *
 * Uso básico (solo fecha):
 *   <Calendar label="Fecha" value={val} onChange={setVal} />
 *
 * Con hora:
 *   <Calendar label="Inicio" value={val} onChange={setVal} withTime />
 *
 * Con validación:
 *   <Calendar label="Vencimiento" value={val} onChange={setVal}
 *             error={!!errors.fecha} errorMessage={errors.fecha} />
 * ─────────────────────────────────────────────────────────────────
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import Text from "../Text/Text";
import "./Calendar.scss";

const DAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const pad = (n) => String(n).padStart(2, "0");

/** Normaliza cualquier formato de entrada a "yyyy-mm-dd hh:mm:ss" */
const normalizeValue = (value) => {
    if (!value) return "";
    if (value instanceof Date) {
        if (isNaN(value)) return "";
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
    }
    if (typeof value !== "string") return "";
    const str = value.trim();
    if (!str) return "";
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str + " 00:00:00";
    if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
        const d = new Date(str);
        if (isNaN(d)) return "";
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }
    return "";
};

const toDisplay = (value, wt, ot = false) => {
    if (ot) {
        if (!value) return "";
        const str = typeof value === "string" ? value.trim() : "";
        if (!str) return "";
        if (/^\d{2}:\d{2}:\d{2}$/.test(str)) return str;
        const normalized = normalizeValue(str);
        if (!normalized) return "";
        return normalized.split(" ")[1] || "";
    }
    const str = normalizeValue(value);
    if (!str) return "";
    const [dp, tp = "00:00:00"] = str.split(" ");
    const [y, m, d] = (dp || "").split("-");
    if (!y || !m || !d) return "";
    return wt ? `${d}/${m}/${y} ${tp}` : `${d}/${m}/${y}`;
};

const toOutput = (y, mo, d, h, mi, s) =>
    `${y}-${pad(mo + 1)}-${pad(d)} ${pad(h)}:${pad(mi)}:${pad(s)}`;

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
};

const PATTERN_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const PATTERN_DATETIME = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;
const PATTERN_TIME = /^(\d{2}):(\d{2}):(\d{2})$/;

const parseDisplay = (text, wt, ot = false) => {
    if (ot) {
        const m = text.match(PATTERN_TIME);
        if (!m) return null;
        const [h, mi, s] = [Number(m[1]), Number(m[2]), Number(m[3])];
        if (h > 23 || mi > 59 || s > 59) return null;
        return `${m[1]}:${m[2]}:${m[3]}`;
    }
    const m = wt ? text.match(PATTERN_DATETIME) : text.match(PATTERN_DATE);
    if (!m) return null;
    const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
    // Validar rango de mes y año mínimo
    if (mo < 1 || mo > 12 || y < 1000) return null;
    // Validar día según el mes real (considera años bisiestos)
    const maxDay = new Date(y, mo, 0).getDate();
    if (d < 1 || d > maxDay) return null;
    if (wt) {
        const [h, mi, s] = [Number(m[4]), Number(m[5]), Number(m[6])];
        if (h > 23 || mi > 59 || s > 59) return null;
        return `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}:${m[6]}`;
    }
    return `${m[3]}-${m[2]}-${m[1]}`;
};

const Calendar = ({ label,classNameWrapper,style, value, onChange, disabled = false, error = false, errorMessage = "Fecha inválida", withTime = false, onlyTime = false }) => {
    const now = new Date();
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const [view, setView] = useState("days");
    const [inputText, setInputText] = useState(() => toDisplay(value, withTime, onlyTime));
    const [internalError, setInternalError] = useState(false);
    const ref = useRef(null);
    const isOpenRef = useRef(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

    const calcPos = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom - 14, left: rect.left });
    };

    const parseValue = () => {
        if (onlyTime) {
            const str = typeof value === "string" ? value.trim() : "";
            if (str && PATTERN_TIME.test(str)) {
                const [h, mi, s] = str.split(":").map(Number);
                return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate(), hour: h || 0, min: mi || 0, sec: s || 0 };
            }
            return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate(), hour: 0, min: 0, sec: 0 };
        }
        const str = normalizeValue(value);
        if (!str) return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate(), hour: 0, min: 0, sec: 0 };
        const [dp, tp = "00:00:00"] = str.split(" ");
        const [y, mo, d] = (dp || "").split("-").map(Number);
        const [h, mi, s] = (tp || "00:00:00").split(":").map(Number);
        if (!y || !mo || !d) return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate(), hour: 0, min: 0, sec: 0 };
        return { year: y, month: mo - 1, day: d, hour: h || 0, min: mi || 0, sec: s || 0 };
    };

    const parsed = parseValue();
    const [cursor, setCursor] = useState({ year: parsed.year, month: parsed.month });
    const [tempTime, setTempTime] = useState({ hour: parsed.hour, min: parsed.min, sec: parsed.sec });

    useEffect(() => {
        setInputText(toDisplay(value, withTime, onlyTime));
        if (value) setInternalError(false);
    }, [value]);

    useEffect(() => {
        const handleOutside = (e) => {
            if (isOpenRef.current &&
                ref.current && !ref.current.contains(e.target) &&
                !(dropdownRef.current && dropdownRef.current.contains(e.target))) {
                isOpenRef.current = false;
                setOpen(false);
                setClosing(true);
            }
        };
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    useEffect(() => {
        if (!open) return;
        const update = () => calcPos();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [open]);

    // Flip vertical y ajuste horizontal si el dropdown se sale de la pantalla
    useLayoutEffect(() => {
        if (!open || !dropdownRef.current || !ref.current) return;
        const dropRect = dropdownRef.current.getBoundingClientRect();
        const rect = ref.current.getBoundingClientRect();
        let top = dropdownPos.top;
        let left = dropdownPos.left;
        if (dropRect.bottom > window.innerHeight - 8) {
            top = rect.top - dropRect.height + 14;
        }
        if (dropRect.right > window.innerWidth - 40) {
            left = window.innerWidth - dropRect.width - 40;
        }
        if (top !== dropdownPos.top || left !== dropdownPos.left) {
            setDropdownPos({ top, left });
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            const p = parseValue();
            setCursor({ year: p.year, month: p.month });
            setTempTime({ hour: p.hour, min: p.min, sec: p.sec });
            setView("days");
        }
    }, [open]);

    const handleClose = () => {
        if (!isOpenRef.current) return;
        isOpenRef.current = false;
        setOpen(false);
        setClosing(true);
    };

    const handleAnimEnd = () => { if (closing) setClosing(false); };

    const handleTextChange = (e) => {
        const digitsBeforeCursor = e.target.value.slice(0, e.target.selectionStart).replace(/[^\d]/g, "").length;
        const raw = e.target.value.replace(/[^\d]/g, "");
        let text = raw;

        if (onlyTime) {
            // hh:mm:ss
            if (raw.length > 2) text = raw.slice(0, 2) + ":" + raw.slice(2);
            if (raw.length > 4) text = text.slice(0, 5) + ":" + raw.slice(4);
            text = text.slice(0, 8);
        } else if (withTime) {
            // dd/mm/yyyy hh:mm:ss
            if (raw.length > 2)  text = raw.slice(0, 2) + "/" + raw.slice(2);
            if (raw.length > 4)  text = text.slice(0, 5) + "/" + raw.slice(4);
            if (raw.length > 8)  text = text.slice(0, 10) + " " + raw.slice(8);
            if (raw.length > 10) text = text.slice(0, 13) + ":" + raw.slice(10);
            if (raw.length > 12) text = text.slice(0, 16) + ":" + raw.slice(12);
            text = text.slice(0, 19);
        } else {
            // dd/mm/yyyy
            if (raw.length > 2) text = raw.slice(0, 2) + "/" + raw.slice(2);
            if (raw.length > 4) text = text.slice(0, 5) + "/" + raw.slice(4);
            text = text.slice(0, 10);
        }

        setInputText(text);
        setInternalError(false);
        if (!text) { onChange?.("" ); return; }
        const out = parseDisplay(text, withTime, onlyTime);
        if (out) onChange?.(out);

        // Restaurar posición del cursor según dígitos antes del cursor
        requestAnimationFrame(() => {
            if (!inputRef.current) return;
            let digitCount = 0;
            let newPos = text.length;
            for (let i = 0; i < text.length; i++) {
                if (/\d/.test(text[i])) {
                    digitCount++;
                    if (digitCount === digitsBeforeCursor) { newPos = i + 1; break; }
                }
            }
            inputRef.current.setSelectionRange(newPos, newPos);
        });
    };

    const handleBlur = (e) => {
        if (ref.current && ref.current.contains(e.relatedTarget)) return;
        if (inputText && !parseDisplay(inputText, withTime, onlyTime)) setInternalError(true);
    };

    const changeTime = (key, max, dir) => {
        setTempTime(prev => {
            const next = dir === 1 ? (prev[key] >= max ? 0 : prev[key] + 1) : (prev[key] <= 0 ? max : prev[key] - 1);
            const newTime = { ...prev, [key]: next };
            if (onlyTime) {
                onChange?.(`${pad(newTime.hour)}:${pad(newTime.min)}:${pad(newTime.sec)}`);
            } else {
                const p = parseValue();
                onChange?.(toOutput(p.year, p.month, p.day, newTime.hour, newTime.min, newTime.sec));
            }
            return newTime;
        });
    };

    const selectDay = (day) => {
        const out = withTime
            ? toOutput(cursor.year, cursor.month, day, tempTime.hour, tempTime.min, tempTime.sec)
            : `${cursor.year}-${pad(cursor.month + 1)}-${pad(day)}`;
        onChange?.(out);
        if (!withTime) handleClose();
    };

    const prevMonth = () => setCursor(c => {
        const m = c.month === 0 ? 11 : c.month - 1;
        const y = c.month === 0 ? c.year - 1 : c.year;
        return { year: y, month: m };
    });

    const nextMonth = () => setCursor(c => {
        const m = c.month === 11 ? 0 : c.month + 1;
        const y = c.month === 11 ? c.year + 1 : c.year;
        return { year: y, month: m };
    });

    const daysInMonth = getDaysInMonth(cursor.year, cursor.month);
    const firstDay = getFirstDayOfMonth(cursor.year, cursor.month);
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;

    const isSelected = (d) => {
        const p = parseValue();
        return value && p.year === cursor.year && p.month === cursor.month && p.day === d;
    };
    const isToday = (d) => now.getFullYear() === cursor.year && now.getMonth() === cursor.month && now.getDate() === d;

    const years = Array.from({ length: 20 }, (_, i) => cursor.year - 5 + i);
    const showError = error || internalError;
    const showErrorMessage = internalError ? (onlyTime ? "Hora inválida" : "Fecha inválida") : errorMessage;

    return (
        <div style={style}  className={`input-field Calendar ${showError ? "has-error" : ""} ${open ? "is-open" : ""} animate__animated animate__fadeInUp`} ref={ref}>
            {label && <Text size="small" color="var(--gray-dark-500)" className="input-label">{label}</Text>}
            <div className={`input-wrapper ${classNameWrapper?classNameWrapper:""}`}>
                <span className="icon input-icon" data-icon={onlyTime ? "schedule" : "calendar_month"} />
                <input
                    ref={inputRef}
                    className={`input-element has-left-icon ${disabled ? "disabled" : ""}`}
                    type="text"
                    placeholder={onlyTime ? "hh:mm:ss" : withTime ? "dd/mm/aaaa hh:mm:ss" : "dd/mm/aaaa"}
                    value={inputText}
                    onChange={handleTextChange}
                    onClick={() => {
                        if (!disabled && !isOpenRef.current && !closing) {
                            calcPos();
                            isOpenRef.current = true;
                            setOpen(true);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            e.preventDefault();
                            handleClose();
                            const focusables = Array.from(
                                document.querySelectorAll(
                                    'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
                                )
                            );
                            const idx = focusables.indexOf(inputRef.current);
                            if (idx !== -1 && focusables[idx + 1]) focusables[idx + 1].focus();
                        }
                    }}
                    onBlur={handleBlur}
                    disabled={disabled}
                />
                {inputText && !disabled && (
                    <button className="preview icon" data-icon="close" type="button"
                        onClick={() => { setInputText(""); setInternalError(false); onChange?.(""); }} />
                )}
            </div>
            {showError && <Text className="input-error" color="var(--red-600)" size="small">{showErrorMessage}</Text>}

            {(open || closing) && createPortal(
                <div className="Calendar" style={{ position: "fixed", top: 0, left: 0, width: 0, height: 0, overflow: "visible", zIndex: 9999, pointerEvents: "none" }}>
                <div
                    ref={dropdownRef}
                    className={`cal-dropdown ${!onlyTime && withTime && view === "days" ? "with-time" : ""} ${onlyTime ? "only-time" : ""} ${closing ? "cal-closing" : "cal-opening"}`}
                    style={{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, pointerEvents: "all" }}
                    onAnimationEnd={handleAnimEnd}
                    onMouseDown={e => e.preventDefault()}
                >
                    {!onlyTime && (
                    <div className="cal-main">
                        {view === "days" && (
                            <>
                                <div className="cal-nav">
                                    <button type="button" onClick={prevMonth}><span className="icon" data-icon="chevron_left" /></button>
                                    <div className="cal-nav-center">
                                        <button type="button" className="cal-month-btn" onClick={() => setView("months")}>{MONTHS[cursor.month]}</button>
                                        <button type="button" className="cal-year-btn" onClick={() => setView("years")}>{cursor.year}</button>
                                    </div>
                                    <button type="button" onClick={nextMonth}><span className="icon" data-icon="chevron_right" /></button>
                                </div>
                                <div className="cal-grid">
                                    {DAYS.map(d => <span key={d} className="cal-weekday">{d}</span>)}
                                    {Array.from({ length: totalCells }, (_, i) => {
                                        const day = i - firstDay + 1;
                                        const valid = day >= 1 && day <= daysInMonth;
                                        return (
                                            <button key={i} type="button"
                                                className={`cal-day ${!valid ? "empty" : ""} ${valid && isToday(day) ? "today" : ""} ${valid && isSelected(day) ? "selected" : ""}`}
                                                onClick={() => valid && selectDay(day)} disabled={!valid}>
                                                {valid ? day : ""}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {view === "months" && (
                            <div className="cal-picker-grid">
                                <div className="cal-picker-title">
                                    <button type="button" onClick={() => setView("days")}><span className="icon" data-icon="arrow_back" /></button>
                                    <span>{cursor.year}</span>
                                </div>
                                <div className="cal-picker-items">
                                    {MONTHS.map((m, i) => (
                                        <button key={m} type="button"
                                            className={`cal-picker-item ${cursor.month === i ? "selected" : ""}`}
                                            onClick={() => { setCursor(c => ({ ...c, month: i })); setView("days"); }}>
                                            {m.slice(0, 3)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {view === "years" && (
                            <div className="cal-picker-grid">
                                <div className="cal-picker-title">
                                    <button type="button" onClick={() => setView("days")}><span className="icon" data-icon="arrow_back" /></button>
                                    <span>Año</span>
                                </div>
                                <div className="cal-picker-items">
                                    {years.map(y => (
                                        <button key={y} type="button"
                                            className={`cal-picker-item ${cursor.year === y ? "selected" : ""}`}
                                            onClick={() => { setCursor(c => ({ ...c, year: y })); setView("days"); }}>
                                            {y}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                    {(onlyTime || (withTime && view === "days")) && (
                        <div className="cal-time-panel">
                            <span className="cal-time-title">Hora</span>
                            <div className="cal-time-cols">
                                {[
                                    { label: "HH", key: "hour", max: 23 },
                                    { label: "MM", key: "min",  max: 59 },
                                    { label: "SS", key: "sec",  max: 59 },
                                ].map(({ label, key, max }) => (
                                    <div key={key} className="cal-time-col">
                                        <button type="button" onClick={() => changeTime(key, max, 1)}><span className="icon" data-icon="expand_less" /></button>
                                        <span className="cal-time-value">{pad(tempTime[key])}</span>
                                        <button type="button" onClick={() => changeTime(key, max, -1)}><span className="icon" data-icon="expand_more" /></button>
                                        <span className="cal-time-label">{label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Calendar;
